import Layers from "slotworks/graphics/layers/layers";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import * as TweenJS from "slotworks/utils/tween";
import deviceInfo from "slotworks/utils/device-info";
import gameState from "slotworks/model/game-state";
import translations from "slotworks/i18n/translations";
import FooterComponent from "slotworks/components/info/footer-component";
import currency from "slotworks/i18n/currency";
import Timer from "slotworks/utils/timer";
import settingsModel from "slotworks/model/settings/settings-model";

const SCROLL = 0;
const SCATTER = 1;
const WAYS = 2;

const DEFAULT_FONT = "roboto-bold";

export default class MessagesComponent extends FooterComponent {

    public static TIME = 15000;
    public static SWITCH_TIME = 10000;

    private layer: Layers;
    private bowlLayer: Layers;

    private bowl: PIXI.Sprite = null;
    private scatterText: PIXI.Text = null;
    private scrollText: PIXI.Text = null;
    private basicText: PIXI.Text = null;

    private stack1Text: PIXI.Text = null;
    private stack2Text: PIXI.Text = null;

    private scatter1Text: PIXI.Text = null;
    private scatter2Text: PIXI.Text = null;
    private scatter: PIXI.Sprite = null;

    private mask: PIXI.Graphics = null;

    private landscapeStateOn: boolean = true;

    private timer: number;
//    private tween: TweenJS.Tween = null;
    private tweens: TweenJS.Tween[] = [];

    constructor() {
        super();
        Layers.MatrixBackground.onSceneEnter.removeAll(); 

        this.layer = Layers.Footer;
        this.bowlLayer = Layers.MatrixBackground;
        this.reset();
        this.resetBowl();
        this.layer.onSceneEnter.add(() => this.reset());
        this.bowlLayer.onSceneEnter.add(() => this.resetBowl());

        if(!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.add(this.onOrientationChange);            
        }
        this.onOrientationChange();
        this.showDefaultText();
    }

    public showBlank() {
        this.setWaysText("");
    }

    public showNoWin() {
        this.setWaysText(translations.get("fot.no"));
    }

    public showSpinStart(betValue: number) {
        this.setWaysText(translations.get("fot.pla", {bet: currency.format(betValue)}));
    }

    public showFreespinStart() {
        const id = "fot.fpl";
        const message = translations.get(id);

        if (id !== message) {
            this.setWaysText(message);
        }
    }

    public showGameWin(winValue: number) {
        this.setWaysText(translations.get("fot.gam", {win: currency.format(winValue)}));
    }

    public showLineWin(lineValue: number, winValue: number, symbol?: string, matches?: number) {
        const win = currency.format(winValue);
        this.setWaysText(translations.get("way_wins_caps") + " " + win);
    }

    public showCustomMessage(message: string) {
        this.setWaysText(message);
    }

    public showDefaultText() {
        this.setWaysText(translations.get("fot.new"));
    }

    public onEnterFreespins() {
        if(canvasManager.orientation === Orientation.PORTRAIT) {
            this.setPortraitScatterText(true);
        } else {
            this.setLandscapeScatterText(true);
        }
    }

    public onExitFreespins() {
        if(canvasManager.orientation === Orientation.PORTRAIT) {
            this.setPortraitScatterText(false);
        } else {
            this.setLandscapeScatterText(false);
        }
    }

    private clean() {
        Timer.clearTimeout(this.timer);
        this.cleanTweens();
    }

    private cleanTweens() {
        for(let tween of this.tweens) {
            if(tween) {
                tween.stop();
                TweenJS.remove(tween);
            }
        }
        this.tweens.length = 0;
    }

    private next = (instant: boolean = false) => {
        if(canvasManager.orientation == Orientation.LANDSCAPE) {
            this.cleanTweens();
            Timer.clearTimeout(this.timer);            
            if(instant) {
                this.landscapeStateOn = Math.random() < 0.5;
            }
            if(this.landscapeStateOn) {
                if(instant) {
                    this.scatter1Text.alpha = 1;
                    this.scatter2Text.alpha = 1;
                    this.scatter.alpha = 1;
                    this.stack1Text.alpha = 0;
                    this.stack2Text.alpha = 0;
                } else {
                    this.tweens.push(this.fade([this.scatter1Text, this.scatter2Text, this.scatter], 400, 200, false));
                    this.tweens.push(this.fade([this.stack1Text, this.stack2Text], 400, 0, true));
                }
            } else {                
                if(instant) {
                    this.stack1Text.alpha = 1;
                    this.stack2Text.alpha = 1;
                    this.scatter1Text.alpha = 0;
                    this.scatter2Text.alpha = 0;
                    this.scatter.alpha = 0;
                } else {
                    this.tweens.push(this.fade([this.stack1Text, this.stack2Text], 400, 200, false));
                    this.tweens.push(this.fade([this.scatter1Text, this.scatter2Text, this.scatter], 400, 0, true));
                }
                
            }
            this.landscapeStateOn = !this.landscapeStateOn;
            this.timer = Timer.setTimeout(this.next, MessagesComponent.SWITCH_TIME);
        }
    }

    private fade(elements: PIXI.Sprite[], time: number, delay: number, out: boolean) {
        const obj = { alpha: out? 1 : 0};
        const update = () => {
            for(let el of elements) {
                el.alpha = obj.alpha;
            }
        }
        const tween = new TweenJS.Tween(obj)
        .to({ alpha: out? 0 : 1}, time)
        .onUpdate(update)
        .delay(delay)
        .start();
        return tween;
    }

    private onOrientationChange = () => {
        if(canvasManager.orientation == Orientation.PORTRAIT) {
            this.fixMessageElements();
            this.clean();
            this.showDefaultText();
        } else {
            this.fixLandscapeElements();
            this.clean();
            this.showDefaultText();
            this.next(true);
        }
    }

    private fixLandscapeElements = () => {
        if(canvasManager.orientation == Orientation.LANDSCAPE) {
            Layers.Custom.get("MessageElements").setScene("landscape").execute();
            this.scatter1Text = Layers.Custom.get("MessageElements").getText("triggers1");
            this.scatter2Text = Layers.Custom.get("MessageElements").getText("triggers2");
            this.stack1Text = Layers.Custom.get("MessageElements").getText("stack1");
            this.stack2Text = Layers.Custom.get("MessageElements").getText("stack2");
            this.scatter = Layers.Custom.get("MessageElements").getText("scatter");
            this.setLandscapeScatterText(gameState.getCurrentGame().inFreespinsGame);

            this.stack1Text.text = translations.get("feature_message_stack_trigger_pt1");
            this.stack2Text.text = translations.get("feature_message_stack_trigger_pt2");
        }
    }

    private setLandscapeScatterText(freespins: boolean) {
        if(freespins) {
            if(this.scatter1Text) this.scatter1Text.text = translations.get("feature_message_fs_retrigger_pt1");
            if(this.scatter2Text) this.scatter2Text.text = translations.get("feature_message_fs_retrigger_pt2");
        } else {
            if(this.scatter1Text) this.scatter1Text.text = translations.get("feature_message_fs_trigger_pt1");
            if(this.scatter2Text) this.scatter2Text.text = translations.get("feature_message_fs_trigger_pt2");
        }
    }

    private fixMessageElements = () => {
        if(canvasManager.orientation == Orientation.PORTRAIT) {
            Layers.Custom.get("MessageElements").setScene("portrait").execute();
            const bowl = Layers.Custom.get("MessageElements").getSprite("bowl");
            bowl.anchor.set(0.5, 0.5);
            bowl.x += bowl.width/2; bowl.y += bowl.height/2;
            bowl.scale.set(1.2, 1.2);

            this.setPortraitScatterText(gameState.getCurrentGame().inFreespinsGame);
            const textUnder = Layers.Custom.get("MessageElements").getText("text_under");
            if(textUnder) {
                textUnder.text = translations.get("feature_message_stack_trigger_pt1")  + ' ' + translations.get("feature_message_stack_trigger_pt2");
                if(this.isFontChangeLanguage()) {
                    textUnder.style.fontFamily = DEFAULT_FONT;
                }
            }
        }
    }

    private isFontChangeLanguage() {
        return settingsModel.language == "ru" || settingsModel.language == "lv";
    }

    private setPortraitScatterText(freespins: boolean) {
        const textTop = Layers.Custom.get("MessageElements").getText("text_top");
        if(textTop) {
            if(freespins) {
                textTop.text = translations.get("feature_message_fs_retrigger_pt1") + ' ' + translations.get("feature_message_fs_retrigger_pt2");
                if(this.isFontChangeLanguage()) {
                    textTop.style.fontFamily = DEFAULT_FONT;
                }
            } else {
                textTop.text = translations.get("feature_message_fs_trigger_pt1") + ' ' + translations.get("feature_message_fs_trigger_pt2");
                if(this.isFontChangeLanguage()) {
                    textTop.style.fontFamily = DEFAULT_FONT;
                }
            }
        }
    }
    
    private setWaysText(message: string = "") {
        this.hideAll();
        if(this.basicText) {
            this.basicText.visible = true;
            this.basicText.text = message;
        }
    }

    private drawMask() {
        const maskPosition1 = this.layer.getPosition('mask');
        const maskPosition2 = this.layer.getPosition('mask2');
        this.mask = this.mask || new PIXI.Graphics();
        this.mask.clear();
        this.mask.beginFill(0xff0000);
        this.mask.drawRect(maskPosition1.x + this.layer.container.x, 
               maskPosition1.y + this.layer.container.y, maskPosition1.width, maskPosition1.height);
        this.mask.drawRect(maskPosition2.x + this.layer.container.x, 
                maskPosition2.y + this.layer.container.y, maskPosition2.width, maskPosition2.height);
        this.layer.container.mask = this.mask;
        canvasManager.stage.addChild(this.mask)
    }

    private reset() {
        this.scatterText = this.layer.getText('scatter');
        this.scrollText = this.layer.getText('scrollText');
        this.basicText = this.layer.getText('basic');

        if(this.isFontChangeLanguage() && this.basicText) {
            this.basicText.style.fontFamily = DEFAULT_FONT;
        }

        this.drawMask();
        this.hideAll();
    }

    private resetBowl() {
        this.bowl = this.bowlLayer.getSprite('bowl');
        this.bowl.visible = false;
    }

    private hideAll() {
        if (this.scatterText) {
            this.scatterText.visible = false;
        } 
        if(this.scrollText) {
            this.scrollText.visible = false;
        }
        if(this.basicText) {
            this.basicText.visible = false;
        }
        if(this.bowl) {
            this.bowl.visible = false;
        }
    }

}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/messages/messages-component.ts